/* eslint-disable no-param-reassign */
import React, { ReactNode, Ref, forwardRef, TextareaHTMLAttributes, useLayoutEffect, useRef, useEffect } from 'react';
import { classNames } from 'primereact/utils';
import { normalizeInputValue } from 'utils/text';
import { AutosaveIndicator, AutosaveIndicatorRef } from 'components/_new/AutosaveIndicator';
import { getInputBaseClassName } from '../InputBase';
import './InputTextArea.scss';

export type InputTextAreaProps = Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'value'> & {
	variant?: 'white';
	value?: string | number | null;
	autoResize?: boolean;
	oneLine?: boolean;
	invalid?: boolean;
	autosaveMessage?: ReactNode;
	autosaveIndicatorRef?: React.Ref<AutosaveIndicatorRef>;
	autosaveIndicatorClassName?: string;
};

export const InputTextArea = forwardRef(
	(
		{
			variant,
			className,
			value,
			autoResize,
			style,
			oneLine = false,
			invalid = false,
			autosaveMessage,
			autosaveIndicatorRef,
			autosaveIndicatorClassName,
			...restProps
		}: InputTextAreaProps,
		ref: Ref<HTMLTextAreaElement>
	) => {
		const internalRef = useRef<HTMLTextAreaElement | null>(null);

		const handleAutoResize = () => {
			if (autoResize) {
				requestAnimationFrame(() => {
					if (internalRef.current) {
						internalRef.current.style.height = 'auto';
						internalRef.current.style.height = `${internalRef.current.scrollHeight}px`;
					}
				});
			}
		};

		useLayoutEffect(() => {
			handleAutoResize();
		}, [value, autoResize]);

		useEffect(() => {
			if (autoResize) {
				window.addEventListener('input', handleAutoResize);
				window.addEventListener('resize', handleAutoResize);
				return () => {
					window.removeEventListener('input', handleAutoResize);
					window.removeEventListener('resize', handleAutoResize);
				};
			}
			return undefined;
		}, [autoResize]);

		const setRef = (node: HTMLTextAreaElement | null) => {
			internalRef.current = node;
			if (typeof ref === 'function') {
				ref(node);
			} else if (ref && 'current' in ref) {
				// eslint-disable-next-line no-param-reassign
				(ref as any).current = node;
			}
		};

		const textareaStyle: React.CSSProperties = {
			...style,
			...(autoResize ? { overflow: 'hidden', resize: 'none' as any } : {}),
		};

		return (
			<div className="input-text-area-autosave-wrapper">
				{autosaveMessage && (
					<AutosaveIndicator
						ref={autosaveIndicatorRef}
						absolute
						message={autosaveMessage}
						className={autosaveIndicatorClassName}
					/>
				)}
				<div
					className={classNames(
						getInputBaseClassName({ invalid }),
						'input-text-area',
						{
							'input-text-area-min': oneLine,
						},
						className
					)}
				>
					<textarea ref={setRef} value={normalizeInputValue(value)} style={textareaStyle} {...restProps} />
				</div>
			</div>
		);
	}
);
